import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
// @ts-ignore
import { IdentityContext } from "./IdentityProvider";
import { useExperimentsContext } from "./ExperimentsProvider";
import { RevisedDesignFirstFlowContext } from "../utils/abTests/abTests";
import { getDesignsOptions, getGalleryConfig } from "../services/GalleryContentService";
import { MerchandisingProductView } from "../types/ContentfulTypes";
import { DesignOptionKey } from "../types/DesignOptionTypes";
import { ColorSwatch, GalleryContentType, TemplateInfo } from "../types/GalleryContentServiceTypes";
import { ProductContext } from "./ProductContextProvider";
import { getColorsFromUrl, getQueryParametersFromUrl } from "../utils/urlHelper";
import { ProductConfiguration } from "@vp/hamilton-service-clients/dist/src/types/Pricing";
import { isEmpty, isEqual, isObject } from "lodash";

export interface GalleryTemplateContextType {
  designId: string;
  templateToken: string;
  previewUrl: string;
  fullProductOptions: ProductConfiguration;
  compatibleColors: Record<string, string[]>;
}

export const GalleryTemplateContext = createContext<GalleryTemplateContextType>({
  designId: "",
  templateToken: "",
  previewUrl: "",
  fullProductOptions: {},
  compatibleColors: {},
});

export interface GalleryTemplateContextProps {
  locale: string;
  mpv: MerchandisingProductView;
}

export const GalleryTemplateProvider = (props: PropsWithChildren<GalleryTemplateContextProps>) => {
  const { locale, mpv } = props;
  const { auth, isIdentityInitialized } = useContext(IdentityContext);
  const { designOptions = {}, selectDesignOptions, selections, selectCompatibleValue } = useContext(
    ProductContext
  );
  const designColor = designOptions[DesignOptionKey.DESIGN_COLOR];
  const { useExperiment } = useExperimentsContext();
  const revisedDesignFirstFlowVariation = useExperiment(RevisedDesignFirstFlowContext);
  const designId = getQueryParametersFromUrl().designId;

  const [galleryTemplateState, setGalleryTemplateState] = useState({
    templateToken: "",
    previewUrl: "",
    fullProductOptions: {},
    compatibleColors: {},
  });
  const configDeepCompareMemoize = (value: any) => {
    const ref = useRef(value);

    if (!isEqual(value, ref.current)) {
      ref.current = value;
    }

    return ref.current;
  };

  useEffect(() => {
    let mounted = true;
    if (revisedDesignFirstFlowVariation === "test" && isIdentityInitialized) {
      const fetchData = async () => {
        try {
          const galleryConfig = await getGalleryConfig(mpv.mpvId, locale, auth);

          if (galleryConfig) {
            const galleryTemplate = await getDesignsOptions(
              mpv.version,
              galleryConfig.galleryName,
              locale,
              auth,
              designId
            );

            if (galleryTemplate) {
              const { Backside, Trim } = getQueryParametersFromUrl();
              let fullProductOptions = galleryTemplate?.results?.content[0]?.fullProductOptions;
              if (
                Backside &&
                Trim &&
                isObject(fullProductOptions) &&
                !isEmpty(fullProductOptions)
              ) {
                fullProductOptions = {
                  ...fullProductOptions,
                  ...selections,
                  Backside,
                  Trim,
                };
              }
              const templateInfoResponse = fetchTemplateInfoForColor(galleryTemplate);

              const previewUrl =
                templateInfoResponse.previewUrls?.size2x ||
                templateInfoResponse.previewUrls?.size1x;
              if (mounted) {
                if (templateInfoResponse?.designColor) {
                  selectDesignOptions({
                    [DesignOptionKey.DESIGN_COLOR]: templateInfoResponse.designColor,
                  });
                }

                setGalleryTemplateState(
                  (prevState: Omit<GalleryTemplateContextType, "designId">) => ({
                    ...prevState,
                    fullProductOptions,
                    templateToken: templateInfoResponse.templateToken,
                    previewUrl,
                    compatibleColors: {
                      [DesignOptionKey.DESIGN_COLOR]: templateInfoResponse.compatibleValues,
                    },
                  })
                );

                selectCompatibleValue({
                  ...fullProductOptions,
                  ...selections,
                });
              }
            }
          }
        } catch (err) {
          console.error("Failed to template details from gallery");
        }
      };
      fetchData();
    }

    return () => {
      mounted = false;
    };
  }, [
    mpv.mpvId,
    locale,
    isIdentityInitialized,
    auth,
    revisedDesignFirstFlowVariation,
    designId,
    configDeepCompareMemoize(designOptions),
    configDeepCompareMemoize(selections),
  ]);

  const fetchTemplateInfoForColor = (data: GalleryContentType): TemplateInfo => {
    const previewUrls = data?.results?.content[0]?.previewUrls;
    let result: ColorSwatch | undefined;
    const currentColorSwatch = getColorsFromUrl(locale)?.options?.find(
      color => color.currentColorSwatch
    )?.key;
    const compatibleValues = data?.results?.content[0].colorSwatches.map(
      (colorSwatch: ColorSwatch) => colorSwatch.color
    );
    if (designColor) {
      result = data?.results?.content[0].colorSwatches.find(
        (colorSwatch: ColorSwatch) => colorSwatch.color === designColor
      );
    } else if (designId && !designColor && !currentColorSwatch) {
      result = data?.results?.content[0].colorSwatches.find(
        (colorSwatch: ColorSwatch) => colorSwatch.isMainSwatch === true
      );
    } else if (currentColorSwatch) {
      result = data?.results?.content[0].colorSwatches.find(
        (colorSwatch: ColorSwatch) => colorSwatch.color === currentColorSwatch
      );
    }
    return {
      previewUrls: result?.previewUrls ?? previewUrls,
      templateToken: result?.previewInfo.templateToken ?? "",
      compatibleValues,
      designColor: result?.color ?? "",
    };
  };

  return (
    <GalleryTemplateContext.Provider value={{ designId, ...galleryTemplateState }}>
      {props.children}
    </GalleryTemplateContext.Provider>
  );
};
